import { ReactNode } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrandLine,
  colorLandingBrandSoft,
  colorLandingBrandStrong,
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingSurface,
  fontLandingDisplay,
  radiusLandingCard,
  shadowLandingFlat,
} from "src/design/tokens";
import { SectionHeading } from "./components/SectionHeading";

const StepIcon = ({ children }: { children: ReactNode }) => (
  <Box
    component="svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    {children}
  </Box>
);

const STEPS = [
  {
    key: "one",
    icon: (
      <StepIcon>
        <path d="M3 9l1.2-4.5A1 1 0 0 1 5.2 4h13.6a1 1 0 0 1 1 .5L21 9" />
        <path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
        <path d="M9 20v-6h6v6" />
      </StepIcon>
    ),
  },
  {
    key: "two",
    icon: (
      <StepIcon>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </StepIcon>
    ),
  },
  {
    key: "three",
    icon: (
      <StepIcon>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" />
      </StepIcon>
    ),
  },
];

/** Onboarding promise: three cards from sign-up to taking reservations. */
export const StepsSection = () => {
  const { t } = useTranslation();

  return (
    <Box
      component="section"
      sx={{ py: { xs: 8, md: 11.5 }, borderTop: `1px solid ${colorLandingLine}` }}
    >
      <Container maxWidth="lg">
        <SectionHeading title={t("landingPage.steps.title")} />

        <Grid container spacing={2.75} sx={{ mt: 3 }}>
          {STEPS.map((step, index) => (
            <Grid item xs={12} md={4} key={step.key}>
              <Box
                sx={{
                  height: "100%",
                  backgroundColor: colorLandingSurface,
                  border: `1px solid ${colorLandingLine}`,
                  borderRadius: radiusLandingCard,
                  boxShadow: shadowLandingFlat,
                  p: 3.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "13px",
                      backgroundColor: colorLandingBrandSoft,
                      color: colorLandingBrandStrong,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    aria-hidden="true"
                    sx={{
                      fontFamily: fontLandingDisplay,
                      color: colorLandingBrandLine,
                      fontSize: 26,
                      lineHeight: 1,
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>

                <Typography
                  component="h4"
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: colorLandingInk,
                    mb: 1,
                  }}
                >
                  {t(`landingPage.steps.${step.key}.title`)}
                </Typography>
                <Typography
                  sx={{ color: colorLandingMuted, fontSize: 14.5, lineHeight: 1.6 }}
                >
                  {t(`landingPage.steps.${step.key}.body`)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
